// SLICE-SECRET-MENU — owner Secret Menu page. Per-program config for the
// off-menu stamp mechanic: a customer who banks enough "secret" (staff-tagged
// off-menu order) stamps by the time they hit the grand prize can claim a
// separate secret reward instead. Mirrors ReferralsPage's shape closely (same
// gating idea — tenant entitlement + program PATCH), with two departures:
//
// 1. Entitlement, shown immediately like Referrals. Referral has its own
//    dedicated `GET /owner/referral` overview route; secret-menu instead
//    piggybacks `GET /owner/auth/me` (already fetched once per session via
//    useOwnerSession) which now also returns `tenant.secret_menu_entitled`.
//    Same immediate locked-state UX as Referrals, no new route needed. The
//    program PATCH still independently enforces entitlement server-side
//    (patchProgram's secret-menu gating block in
//    apps/api/src/services/loyalty/programs.ts, ForbiddenError / 403,
//    message "Secret menu is not enabled for this tenant") — the session
//    check below is a UX head start, not the security boundary. The save
//    handler keeps a reactive fallback for that 403 (flips the same locked
//    state) in case the cached session is stale — e.g. entitlement was
//    revoked mid-session.
//
// 2. Lock-on-first-save. Unlike referral_config (freely editable forever),
//    secret_menu_config's `required_secret_stamps` and `reward_text` lock
//    permanently the moment they're saved with real values — patchProgram
//    sets `locked: true` server-side on every successful save, and rejects
//    any later PATCH that tries to change either field once locked. This
//    keeps a customer's already-earned eligibility from being invalidated by
//    the owner moving the goalposts. Only `enabled` stays editable after
//    that. `locked` itself is never owner-set — it's server-computed, so the
//    form doesn't expose it as an input; we just render the two fields
//    read-only once the fetched config comes back with `locked: true`.

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import {
  DEFAULT_SECRET_MENU_CONFIG,
  SECRET_MENU_MAX_STAMPS,
  SECRET_MENU_MIN_STAMPS,
  SecretMenuConfigSchema,
  secretMenuConfigIssues,
  type SecretMenuConfig,
} from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { useOwnerSession } from '../hooks/useOwnerSession.js'
import { usePatchProgram, useProgram, usePrograms } from '../program/hooks/useProgram.js'
import './secretMenuPage.css'

// `locked` is server-computed and never rendered as an input (see the
// file-header comment), so it's excluded from the RHF-managed shape
// entirely — that also sidesteps a zodResolver/exactOptionalPropertyTypes
// mismatch: SecretMenuConfigSchema's `locked` carries a `.default(false)`,
// which gives it a different (optional) input type than its inferred
// (required) output type, and useForm needs the two to agree.
const SecretMenuFormSchema = SecretMenuConfigSchema.omit({ locked: true })
type SecretMenuFormValues = Omit<SecretMenuConfig, 'locked'>

export function SecretMenuPage() {
  const sessionQ = useOwnerSession()
  const sessionEntitled = sessionQ.data?.tenant.secret_menu_entitled ?? null

  // Multi-program tenants pick which program's secret menu to configure —
  // same picker shape as ReferralsPage (secret-menu, like referral, is a
  // loyalty-only mechanic: patchProgram branches to patchMembershipProgram /
  // patchEventTicketProgram before the secret_menu_config gating even runs).
  const programsQ = usePrograms()
  const activePrograms = (programsQ.data?.programs ?? []).filter(
    (p) => p.status === 'active' && p.pass_type === 'loyalty',
  )
  const multiProgram = activePrograms.length > 1
  const defaultProgram =
    activePrograms.find((p) => p.is_default) ?? activePrograms[0]
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    null,
  )
  const effectiveProgramId = selectedProgramId ?? defaultProgram?.id ?? null

  const programQ = useProgram(effectiveProgramId ?? '')
  const program = effectiveProgramId ? programQ.data : undefined
  const totalStamps = program?.total_stamps ?? SECRET_MENU_MAX_STAMPS
  const stampMax = Math.min(SECRET_MENU_MAX_STAMPS, totalStamps)
  const savedConfig = program?.secret_menu_config ?? DEFAULT_SECRET_MENU_CONFIG
  const locked = savedConfig.locked

  // Reactive fallback: flips true if a save ever comes back with the "not
  // entitled" 403 despite the session saying otherwise (a stale cached
  // session — e.g. entitlement was revoked mid-session). The session check
  // above (`sessionEntitled === false`) is what makes the locked state show
  // immediately on load, same as Referrals; this is just the safety net.
  // Reset on program switch so a stale flip from one program never bleeds
  // into another (entitlement is tenant-wide, not per-program, but cheap
  // safety costs nothing here).
  const [saveRejected, setSaveRejected] = useState(false)
  useEffect(() => {
    setSaveRejected(false)
  }, [effectiveProgramId])
  const notEntitled = sessionEntitled === false || saveRejected

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<SecretMenuFormValues>({
    resolver: zodResolver(SecretMenuFormSchema),
    defaultValues: DEFAULT_SECRET_MENU_CONFIG,
  })
  const enabled = watch('enabled')

  // Re-seed the form whenever the resolved program (or its config) changes —
  // switching programs, first load, or a fresh value landing after a save.
  useEffect(() => {
    reset(savedConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id, savedConfig.locked, savedConfig.enabled])

  const patch = usePatchProgram(effectiveProgramId ?? '')

  function onSubmit(data: SecretMenuFormValues) {
    if (!effectiveProgramId) return

    // Locked fields are read-only in the UI, but don't trust that alone —
    // always send back the persisted values for the two locked fields so a
    // stale form can never trip patchProgram's "can no longer be changed"
    // rejection. `locked` itself is always sent as false: the server
    // recomputes and overwrites it regardless of what's sent (see the
    // service-layer comment linked at the top of this file).
    const payload: SecretMenuConfig = {
      enabled: data.enabled,
      required_secret_stamps: locked
        ? savedConfig.required_secret_stamps
        : data.required_secret_stamps,
      reward_text: locked ? savedConfig.reward_text : data.reward_text,
      locked: false,
    }

    if (payload.enabled) {
      const issues = secretMenuConfigIssues(payload, totalStamps)
      if (issues.length > 0) {
        toast.error(issues[0])
        return
      }
    }

    patch.mutate(
      { secret_menu_config: payload },
      {
        // usePatchProgram's own onSuccess already writes the returned
        // program into the query cache and invalidates the programs list —
        // nothing more to do here on success besides the toast.
        onSuccess: () => {
          toast.success('Secret menu settings saved')
        },
        onError: (err) => {
          // See the file-header comment (point 1): with no overview route to
          // check entitlement up front, a not-entitled tenant's first sign of
          // that is this specific 403. Distinguish it from any other FORBIDDEN
          // by message — this mutation only ever PATCHes secret_menu_config,
          // and patchProgram's secret-menu gating block is the only place
          // that throws ForbiddenError on this call path, so message-matching
          // here is safe (no dedicated error code was added for it).
          if (
            err instanceof OwnerApiError &&
            err.status === 403 &&
            err.message === 'Secret menu is not enabled for this tenant'
          ) {
            // Nothing was persisted — revert the form to the last-known
            // saved state (still the untouched defaults, since this tenant
            // has never had a successful save) so the "enabled" checkbox
            // can't keep showing checked and make it LOOK like the save
            // went through. The toast is easy to miss; this locked state
            // is what actually communicates it.
            reset(savedConfig)
            setSaveRejected(true)
            toast.error(
              "This feature isn't enabled for your account yet — contact support.",
            )
            return
          }
          toast.error(
            err instanceof OwnerApiError
              ? err.userMessage
              : 'Could not save secret menu settings',
          )
        },
      },
    )
  }

  if (programsQ.isLoading || sessionQ.isLoading) {
    return (
      <div className="owner-secret-menu owner-secret-menu-loading">
        Loading…
      </div>
    )
  }
  if (programsQ.isError) {
    return (
      <div className="owner-secret-menu owner-secret-menu-loading">
        Could not load secret menu settings. Pull to refresh.
      </div>
    )
  }

  const header = (
    <header className="owner-secret-menu-header">
      <h1>Secret Menu</h1>
      <p className="owner-secret-menu-subtitle">
        Let staff tag stamps as off-menu orders. A customer who banks enough
        secret stamps by the time they reach the grand prize can claim a
        hidden reward instead of the regular one.
      </p>
    </header>
  )

  // Not entitled — same immediate locked state as Referrals (see point 1 in
  // the file header), shown before the program picker since entitlement is
  // tenant-wide, not per-program.
  if (notEntitled) {
    return (
      <div className="owner-secret-menu">
        {header}
        <div className="owner-secret-menu-locked">
          Secret menu isn’t switched on for your account yet. Message Naya
          support and we’ll enable it for you.
        </div>
      </div>
    )
  }

  return (
    <div className="owner-secret-menu">
      {header}

      {multiProgram && (
        <div
          className="owner-secret-menu-field"
          style={{ marginBottom: 20 }}
        >
          <label
            className="owner-secret-menu-label"
            htmlFor="secret-menu-program"
          >
            Program
          </label>
          <select
            id="secret-menu-program"
            className="owner-secret-menu-input"
            value={effectiveProgramId ?? ''}
            onChange={(e) => setSelectedProgramId(e.target.value)}
          >
            {activePrograms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <p className="owner-secret-menu-hint">
            Secret menu settings are per program. Pick which card this
            applies to.
          </p>
        </div>
      )}

      {effectiveProgramId === null ? (
        <div className="owner-secret-menu-locked">
          Create and activate a loyalty program first, then come back to set
          up the secret menu.
        </div>
      ) : programQ.isLoading ? (
        <div className="owner-secret-menu-loading">Loading…</div>
      ) : (
        <>
          {locked ? (
            <div className="owner-secret-menu-note">
              Locked after first save — the off-menu stamp goal and reward
              can no longer be changed. Only turning the secret menu on or
              off stays editable, so a customer who has already started
              earning toward it is never moved off the goalposts.
            </div>
          ) : (
            <div className="owner-secret-menu-note">
              Heads up: once you save, the off-menu stamp goal and reward
              text lock permanently — only turning the secret menu on or off
              will still be editable afterward.
            </div>
          )}

          <form
            className="owner-secret-menu-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <label className="owner-secret-menu-toggle">
              <input
                type="checkbox"
                id="owner-secret-menu-toggle"
                {...register('enabled')}
              />
              <span>Run a secret menu</span>
            </label>

            <fieldset
              className="owner-secret-menu-fields"
              disabled={!enabled}
            >
              <Field
                label="Off-menu stamp goal"
                hint={`Secret stamps the customer needs this cycle to unlock the hidden reward (max ${stampMax}).`}
              >
                <input
                  type="number"
                  className="owner-secret-menu-input"
                  min={SECRET_MENU_MIN_STAMPS}
                  max={stampMax}
                  readOnly={locked}
                  aria-readonly={locked}
                  {...register('required_secret_stamps', {
                    valueAsNumber: true,
                  })}
                />
                {errors.required_secret_stamps && (
                  <p className="owner-secret-menu-error">
                    {errors.required_secret_stamps.message}
                  </p>
                )}
              </Field>

              <Field
                label="Secret reward"
                hint="What the customer gets instead of the regular grand prize."
              >
                <input
                  type="text"
                  className="owner-secret-menu-input"
                  maxLength={200}
                  readOnly={locked}
                  aria-readonly={locked}
                  placeholder="e.g. Free off-menu item of your choice"
                  {...register('reward_text')}
                />
                {errors.reward_text && (
                  <p className="owner-secret-menu-error">
                    {errors.reward_text.message}
                  </p>
                )}
              </Field>
            </fieldset>

            <button
              type="submit"
              className="owner-secret-menu-save"
              disabled={patch.isPending || isSubmitting || !isDirty}
            >
              {patch.isPending || isSubmitting
                ? 'Saving…'
                : 'Save secret menu settings'}
            </button>
          </form>
        </>
      )}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <div className="owner-secret-menu-field">
      <label className="owner-secret-menu-label">{label}</label>
      {children}
      <p className="owner-secret-menu-hint">{hint}</p>
    </div>
  )
}
